import { line as d3Line } from "d3-shape";

// biome-ignore lint/suspicious/noExplicitAny: d3 curve factory type
type CurveFactory = any;

export interface SeriesPathPoint {
  x: number;
  y: number;
  key: string;
  /**
   * MoveBuddy: whether this datum carried a NUMBER under the series key.
   *
   * bklit read `typeof y === "number" ? yScale(y) ?? 0 : 0`, so a null, a missing key or a string
   * became pixel 0 — the TOP of the plot — and a gap in the series rendered as a record high. This
   * app's rule is that a day without a reading is a break in the line, never a zero, so the point
   * is still emitted (the path generator needs its x to know where the break is) and marked
   * undefined instead. `seriesPathFromPoints` hands the flag to d3's `line.defined`, which emits a
   * fresh `M` after every hole rather than one continuous run.
   */
  defined: boolean;
}

export function computeSeriesPathPoints(
  data: Record<string, unknown>[],
  xAccessor: (datum: Record<string, unknown>) => Date,
  xScale: (value: Date) => number | undefined,
  yScale: (value: number) => number | undefined,
  dataKey: string
): SeriesPathPoint[] {
  return data.map((datum, index) => {
    const xValue = xAccessor(datum);
    const yValue = datum[dataKey];
    // MoveBuddy: `Number.isFinite`, not `typeof === "number"`. A NaN or an Infinity reaching a
    // scale produces `NaN` coordinates, which renders as nothing at all with no error anywhere —
    // an invisible failure rather than a visible break.
    const hasValue = typeof yValue === "number" && Number.isFinite(yValue);
    const scaled = hasValue ? yScale(yValue) : undefined;
    return {
      x: xScale(xValue) ?? 0,
      y: scaled ?? 0,
      key: String(xValue.getTime?.() ?? index),
      defined: hasValue && scaled != null,
    };
  });
}

export function interpolateSeriesPathPoints(
  from: SeriesPathPoint[],
  to: SeriesPathPoint[],
  progress: number
): SeriesPathPoint[] {
  if (progress >= 1) {
    return to;
  }
  if (progress <= 0) {
    return from.length > 0 ? from : to;
  }

  const fromByKey = new Map(from.map((point) => [point.key, point]));

  return to.map((target, index) => {
    const source = fromByKey.get(target.key);
    if (source) {
      return {
        key: target.key,
        x: source.x + (target.x - source.x) * progress,
        y: source.y + (target.y - source.y) * progress,
        // MoveBuddy: the TARGET decides. A point that is about to become a hole is a hole for the
        // whole transition — tweening a line into a gap would draw the line through it on the way.
        defined: target.defined,
      };
    }

    const previousTarget = index > 0 ? to[index - 1] : undefined;
    const previousSource = previousTarget
      ? fromByKey.get(previousTarget.key)
      : undefined;
    const nextTarget = index < to.length - 1 ? to[index + 1] : undefined;
    const nextSource = nextTarget ? fromByKey.get(nextTarget.key) : undefined;
    const anchor = previousSource ?? nextSource ?? from[0] ?? target;

    return {
      key: target.key,
      x: anchor.x + (target.x - anchor.x) * progress,
      y: anchor.y + (target.y - anchor.y) * progress,
      defined: target.defined,
    };
  });
}

export function seriesPathFromPoints(
  points: SeriesPathPoint[],
  curve: CurveFactory
): string {
  if (points.length === 0) {
    return "";
  }

  const generator = d3Line<SeriesPathPoint>()
    .x((point) => point.x)
    .y((point) => point.y)
    // MoveBuddy: this is the whole gap fix on the animated path. d3 starts a new subpath after
    // every undefined point, so a series with a hole draws as two strokes with space between them
    // rather than one stroke dipping to the top of the plot.
    .defined((point) => point.defined)
    .curve(curve);

  return generator(points) ?? "";
}

export function seriesPathTransitionSignature({
  renderData,
  xAccessor,
  dataKey,
  innerWidth,
  xDomainMin,
  xDomainMax,
}: {
  renderData: Record<string, unknown>[];
  xAccessor: (datum: Record<string, unknown>) => Date;
  dataKey: string;
  innerWidth: number;
  xDomainMin: number;
  xDomainMax: number;
}): string {
  const values = renderData.map((datum) => {
    const xValue = xAccessor(datum);
    const yValue = datum[dataKey];
    return `${xValue.getTime()}:${typeof yValue === "number" ? yValue : ""}`;
  });

  return `${innerWidth}|${xDomainMin}|${xDomainMax}|${values.join(",")}`;
}
